import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtUSD, num } from "@/lib/format";
import { toast } from "sonner";
import { Crown, CreditCard, Bookmark } from "lucide-react";

const ROLES = ["Nursing student", "New graduate RN", "Staff RN", "PRN RN", "Travel nurse", "Experienced RN", "Advanced practice nurse"];
const SPECIALTIES = ["Medical-Surgical", "Telemetry", "ICU", "CVICU", "Emergency Department", "Oncology", "Dialysis", "Hospice", "Home Health", "Operating Room", "PACU", "Labor & Delivery", "Pediatrics", "Case Management", "Occupational Health", "Other"];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const p = user?.profile || {};
  const isPro = user?.subscription?.plan === "pro" && user?.subscription?.status === "active";
  const [f, setF] = useState({
    first_name: user?.first_name || "", email: user?.email || "",
    nursing_role: p.nursing_role || "", specialty: p.specialty || "",
    years_experience: p.years_experience ?? "", hourly_rate: p.hourly_rate ?? "",
    weekly_hours: p.weekly_hours ?? "", income_goal: p.income_goal ?? "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        first_name: f.first_name, email: f.email, nursing_role: f.nursing_role || null,
        specialty: f.specialty || null,
        years_experience: f.years_experience !== "" ? num(f.years_experience) : null,
        hourly_rate: f.hourly_rate !== "" ? num(f.hourly_rate) : null,
        weekly_hours: f.weekly_hours !== "" ? num(f.weekly_hours) : null,
        income_goal: f.income_goal !== "" ? num(f.income_goal) : null,
      });
      await refreshUser();
      toast.success("Profile updated!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const manageBilling = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Profile</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]">Your account</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label className="text-sm">First name</Label>
              <Input data-testid="profile-firstname" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-sm">Email</Label>
              <Input data-testid="profile-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-sm">Nursing role</Label>
              <Select value={f.nursing_role} onValueChange={(v) => set("nursing_role", v)}>
                <SelectTrigger data-testid="profile-role" className="mt-1.5 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-sm">Specialty</Label>
              <Select value={f.specialty} onValueChange={(v) => set("specialty", v)}>
                <SelectTrigger data-testid="profile-specialty" className="mt-1.5 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-sm">Years of experience</Label>
              <Input data-testid="profile-years" type="number" value={f.years_experience} onChange={(e) => set("years_experience", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-sm">Hourly rate ($)</Label>
              <Input data-testid="profile-rate" type="number" value={f.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-sm">Weekly hours</Label>
              <Input data-testid="profile-hours" type="number" value={f.weekly_hours} onChange={(e) => set("weekly_hours", e.target.value)} className="mt-1.5 rounded-xl" /></div>
            <div><Label className="text-sm">Income goal ($)</Label>
              <Input data-testid="profile-goal" type="number" value={f.income_goal} onChange={(e) => set("income_goal", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="rounded-full bg-[#18A6A6] px-8 py-6 hover:bg-[#148f8f]">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>

        <div className="space-y-4">
          <div className={`rounded-2xl p-6 shadow-sm ${isPro ? "border border-[#D6A84B]/40 bg-gradient-to-br from-[#0F2747] to-[#16355F] text-white" : "border border-slate-200/80 bg-white"}`}>
            <p className={`text-xs font-bold uppercase tracking-widest ${isPro ? "text-[#D6A84B]" : "text-[#18A6A6]"}`}>Subscription</p>
            <div className="mt-2 flex items-center gap-2">
              {isPro && <Crown className="h-5 w-5 text-[#D6A84B]" />}
              <span className={`font-heading text-2xl font-bold ${isPro ? "text-white" : "text-[#0F2747]"}`} data-testid="profile-plan">
                {isPro ? "RN Level Up Pro" : "Free"}
              </span>
            </div>
            {isPro ? (
              <>
                <p className="mt-1 text-sm text-slate-300 capitalize">{user?.subscription?.billing_interval}ly billing</p>
                <Button onClick={manageBilling} data-testid="manage-billing-btn" variant="secondary" className="mt-4 w-full rounded-full">
                  <CreditCard className="mr-2 h-4 w-4" /> Manage billing
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate("/upgrade")} data-testid="profile-upgrade-btn" className="mt-4 w-full rounded-full bg-[#0F2747] hover:bg-[#16355F]">
                Upgrade to Pro
              </Button>
            )}
          </div>
          <button onClick={() => navigate("/saved")} data-testid="profile-saved-link"
            className="flex w-full items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-5 text-left shadow-sm hover:shadow-md">
            <Bookmark className="h-5 w-5 text-[#18A6A6]" />
            <div><p className="font-semibold text-[#0F2747]">Saved calculations</p><p className="text-xs text-slate-500">Shifts, income plans & comparisons</p></div>
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        RN Level Up is a career and financial planning tool for nurses and is not intended for storing
        patient information or protected health information.
      </p>
    </div>
  );
}
